/**
 * The Pages step's decision rules (VIBE-1916).
 *
 * Kept out of the component because the three-valued save contract is easy to
 * degrade into a two-valued one by accident, and every degradation destroys data
 * silently: these rows are shared with the mvp wizard, so "the user skipped the
 * step" rendered as `[]` unlinks a Page they never touched and nothing anywhere
 * reports an error.
 *
 * The contract, in the order it must be evaluated:
 *   1. `linked_page_ids !== null` — otherwise the baseline is UNKNOWN,
 *   2. `unavailable_linked_page_ids` empty — otherwise it is UNREPRODUCIBLE,
 *   3. only then may an empty selection mean a deliberate clear.
 *
 * `MetaPage.linked` is a checkbox seed and nothing more. When the baseline is
 * unknown it is `false` on every row, so a rule built on it reads as "nothing is
 * linked" — which is exactly the wrong answer, and an undetectable one.
 *
 * The Instagram step (VIBE-1990) has its own twin module, `./metaInstagram`,
 * whose rules are these with the noun swapped. Change one, check the other.
 */

import type {
  MetaPage,
  MetaPagesResponse,
  SaveMetaPayload,
  SelectedMetaPixel,
} from '../../api/client';

/**
 * The seeded baseline: the Pages already linked AND listed by this picker.
 *
 * One rule decides what can be ticked: a Page is selectable if and only if
 * `/pages` listed it. Whether Meta issued a page token for it is not part of the
 * rule — the audit profiles a Page with the user token and never reads the
 * stored page token. A linked Page the list does not contain has no box to seed;
 * it surfaces instead through `unavailableLinkedPageIds`, which also forbids the
 * clear.
 */
export function seedSelectionFromPages(pages: readonly MetaPage[]): string[] {
  return pages.filter((p) => p.linked).map((p) => p.id);
}

/**
 * Set comparison, order-independent and duplicate-safe.
 *
 * Order-independent because the selection order is an artefact of which box the
 * user clicked first; a positional compare would call an untouched selection
 * "changed" after a Deselect-All/Select-All round trip.
 */
export function selectionsDiffer(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return true;
  for (const id of left) if (!right.has(id)) return true;
  return false;
}

/**
 * May an EMPTY selection be read as a deliberate clear?
 *
 * Both conditions are load-bearing and neither implies the other. Without the
 * permission the server returns no pages at all AND reports every linked Page as
 * unavailable — so Skip in that state can never mean clear, which is the whole
 * point of the second condition.
 */
export function canClearPages(resp: MetaPagesResponse): boolean {
  // An ABSENT field is not an empty one. A `/pages` older than VIBE-1916 never
  // mentions unavailable links, and it cannot testify that there are none — so
  // a version-skewed frontend degrades to "we do not offer the clear" rather
  // than to "clear away, nothing is unreachable". `Array.isArray` is the test
  // because it separates a real answer from both `null` and `undefined`.
  if (!Array.isArray(resp.unavailable_linked_page_ids)) return false;
  if (!Array.isArray(resp.linked_page_ids)) return false;
  return resp.unavailable_linked_page_ids.length === 0;
}

/** Everything the Pages step knows, in ONE atom. */
export interface PagesStepState {
  pages: MetaPage[];
  /** False → `pages` is empty because no Graph call was made, not because the
   *  user has none. Offer the second grant. */
  scopeGranted: boolean;
  linkedPageIds: string[] | null;
  /** Linked Pages this picker cannot reproduce. Non-empty → no clear. */
  unavailableLinkedPageIds: string[];
  /** Precomputed so the two-condition gate is evaluated in exactly one place. */
  clearAllowed: boolean;
  /** What was linked when this list was seeded. */
  baseline: string[];
  /** What is ticked now. */
  selected: string[];
}

/** The state before anything has loaded — and after a lookup that FAILED.
 *
 *  `linkedPageIds: null` is the honest answer in both cases, and it is what makes
 *  a failed lookup safe: we know nothing, so Skip cannot mean clear. */
export const EMPTY_PAGES_STEP: PagesStepState = {
  pages: [],
  scopeGranted: true,
  linkedPageIds: null,
  unavailableLinkedPageIds: [],
  clearAllowed: false,
  baseline: [],
  selected: [],
};

/**
 * Build the step's state from a `/pages` response.
 *
 * 🔴 The baseline is established HERE, together with the seed it must be compared
 * against — they are the same array by construction. This is the only place the
 * seed is applied, so there is no path (first load, return from the re-grant,
 * Try again) that can re-seed the checkboxes while keeping a stale baseline. A
 * baseline captured once, at mount, goes wrong on every one of those.
 */
export function pagesStepFromResponse(resp: MetaPagesResponse): PagesStepState {
  // `?? []` / `?? null` are not defensive noise: against a `/pages` that predates
  // VIBE-1916 the old code threw on `.length` INSIDE the load promise, and the
  // whole wizard answered "Failed to load Meta entities" — the user could not
  // connect at all, not even with ad accounts alone. Degrading to "baseline
  // unknown" costs them the clear and nothing else.
  const pages = resp.pages ?? [];
  const baseline = seedSelectionFromPages(pages);
  return {
    pages,
    scopeGranted: resp.pages_scope_granted,
    linkedPageIds: resp.linked_page_ids ?? null,
    unavailableLinkedPageIds: resp.unavailable_linked_page_ids ?? [],
    clearAllowed: canClearPages(resp),
    baseline,
    selected: baseline,
  };
}

/**
 * May the user advance with the CURRENT selection applied?
 *
 * Two independent refusals:
 *  - nothing changed → there is nothing to save, and Skip is the honest exit;
 *  - the selection is empty and a clear is not allowed → an empty selection here
 *    does not mean "unlink everything", it means "we could not tell you what was
 *    linked", so applying it would delete a set we never showed.
 *
 * 🔴 Never `selected.length > 0`. A count-based rule reads as "you may unlink any
 * Pages except the last one" and makes the empty set unreachable — the user can
 * never deliberately clear their links.
 */
export function pagesNextEnabled(state: PagesStepState): boolean {
  if (!selectionsDiffer(state.baseline, state.selected)) return false;
  if (state.selected.length === 0 && !state.clearAllowed) return false;
  return true;
}

/**
 * Linked Pages that pressing Next RIGHT NOW would unlink.
 *
 * 🔴 This is the only honest account of the one state in which the wizard
 * destroys a link. When a linked Page is unreachable the picker cannot show it,
 * so a replace cannot carry it — `["A","C"]` replaces `[A, B]` and `B` is gone
 * for good. The reassurance the step otherwise gives ("unticking won't unlink",
 * "skipping leaves them") is true and, on its own, misleading: it is the only
 * mention of unlinking in the interface and it says unlinking will not happen.
 *
 * Gated on `pagesNextEnabled`, not on `selectionsDiffer` alone: with an empty
 * selection and no clear allowed the save never leaves the browser, so nothing
 * is dropped and warning about it would be a second falsehood.
 */
export function pagesDroppedByReplace(state: PagesStepState): string[] {
  if (!pagesNextEnabled(state)) return [];
  return state.unavailableLinkedPageIds.filter((id) => !state.selected.includes(id));
}

/** What advancing past the Pages step decided. */
export type PagesOutcome = { skipped: true } | { skipped: false; pageIds: string[] };

/**
 * The page key of the save payload — or its ABSENCE.
 *
 * 🔴 A skip sends NO key. Not `null`, and emphatically not `[]`: the server reads
 * a present key as "replace the links with exactly this", so `[]` is a deliberate
 * clear. The difference has to survive JSON, so the key must be missing, not
 * undefined-valued-by-accident.
 */
export function metaPagesSavePatch(outcome: PagesOutcome): { selected_page_ids?: string[] } {
  if (outcome.skipped) return {};
  return { selected_page_ids: outcome.pageIds };
}

/**
 * The whole save payload, built in one place so the three-valued page key is not
 * reassembled in JSX where a stray `selected_page_ids: []` would read as normal.
 *
 * A `null` outcome means the Pages step was never left — treated as a skip,
 * because the user made no statement about their Pages and the safe reading of
 * "no statement" is "change nothing".
 */
export function buildMetaSavePayload(args: {
  sessionId: string;
  adAccountIds: string[];
  pagesOutcome: PagesOutcome | null;
  pixels: SelectedMetaPixel[];
}): SaveMetaPayload {
  return {
    session_id: args.sessionId,
    selected_ad_account_ids: args.adAccountIds,
    ...metaPagesSavePatch(args.pagesOutcome ?? { skipped: true }),
    selected_pixels: args.pixels,
  };
}

/** Toggle one Page. An id that is not on the list is inert: every listed Page is
 *  selectable, and an unknown one is not a listed one. */
export function togglePage(state: PagesStepState, pageId: string): PagesStepState {
  const page = state.pages.find((p) => p.id === pageId);
  // An id we cannot find is NOT a selectable one. The old `page && …` form let an
  // unknown id through, inverting the conservatism the rest of this module rests
  // on — unreachable today, and exactly the kind of default that stops being
  // unreachable without anyone noticing.
  if (!page) return state;
  const selected = state.selected.includes(pageId)
    ? state.selected.filter((id) => id !== pageId)
    : [...state.selected, pageId];
  return { ...state, selected };
}

/** Select-all / deselect-all over the ids currently shown (already filtered by
 *  the search box). */
export function toggleAllPages(state: PagesStepState, visibleIds: readonly string[]): PagesStepState {
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => state.selected.includes(id));
  const selected = allSelected
    ? state.selected.filter((id) => !visibleIds.includes(id))
    : Array.from(new Set([...state.selected, ...visibleIds]));
  return { ...state, selected };
}

// ---------------------------------------------------------------------------
// When the session itself is the problem
// ---------------------------------------------------------------------------

/**
 * HTTP statuses on the Meta OAuth endpoints that mean THIS SESSION is finished.
 *
 * Every one of them comes out of `require_oauth_session`, which guards `/pages`,
 * `/pixels` AND `/save` alike: 404 the session is gone, 410 it expired, 403 it
 * belongs to somebody else, 401 the caller is no longer authenticated. There is
 * no retry that can succeed afterwards — the next `/save` fails identically, and
 * so would a reload of the ad accounts.
 */
export const TERMINAL_SESSION_STATUSES: readonly number[] = [401, 403, 404, 410];

/**
 * Is this failure the end of the session, or something the user can act on?
 *
 * 🔴 The distinction is what keeps `error` meaning exactly one thing: "this
 * session is unusable, for loading and for saving alike". Without it a dead
 * session renders as a dismissible notice over a wizard whose every button leads
 * back to the same message — honest text, no way out. With it, the wizard says
 * so once and stops pretending there is something left to try.
 *
 * Takes a status rather than the error object so this module stays free of any
 * runtime import from `client.ts`.
 */
export function isSessionTerminalStatus(status: number | null | undefined): boolean {
  if (status === null || status === undefined) return false;
  return TERMINAL_SESSION_STATUSES.includes(status);
}

// ---------------------------------------------------------------------------
// What came back from the re-grant round trip
// ---------------------------------------------------------------------------

/**
 * The outcome of an on-demand grant, as the `/connect` redirect reports it.
 *
 * Two grants return through this redirect: the Page grant and, since VIBE-1990,
 * the Instagram step's own grant. Only the Page grant reports `granted` /
 * `denied`; the Instagram one carries no outcome parameter at all (it would be
 * read as a Page result), so it arrives as `none` or `ads_scope_revoked`, and the
 * wizard tells the two grants apart by the draft it parked — never by this value.
 *
 * `ads_scope_revoked` is the branch that is easy to get wrong. Meta shows the
 * WHOLE consent screen again under `auth_type=rerequest`, and an already-granted
 * permission can be UNTICKED there — so the result of a grant can be NARROWER
 * than the request. The server refuses to write that degraded token and keeps the
 * working one, which makes this a non-event for the connection and a real event
 * for the user: nothing broke, nothing changed, and the reason is invisible
 * unless we say it.
 */
export type MetaGrantReturn =
  /** A first connect, no round trip, or a return from the Instagram grant — we
   *  have nothing to report. */
  | { kind: 'none' }
  /** `meta_pages_granted=true`. */
  | { kind: 'granted' }
  /** `meta_pages_granted=false` — the Page permission was refused. */
  | { kind: 'denied' }
  /** `meta_error=ads_scope_revoked` — the ADS permission was unticked, so the
   *  grant was discarded wholesale and the previous token still stands. */
  | { kind: 'ads_scope_revoked' };

/**
 * `meta_error` values that must NOT be rendered as "Meta connection failed".
 *
 * The connection did not fail: the server left the session untouched precisely
 * so the user can carry on. Showing the red banner here would report a broken
 * connection over a working one, and it would sit behind the wizard that is
 * still open and still usable.
 */
export const META_NON_FATAL_ERRORS = {
  adsScopeRevoked: 'ads_scope_revoked',
} as const;

/**
 * Read the three Meta parameters the `/connect` redirect can carry, and decide
 * what they mean — kept pure so the branch that distinguishes "your connection
 * broke" from "your change was declined" is testable.
 *
 * `ads_scope_revoked` is non-fatal ONLY alongside a session. That is what makes
 * it safe: the session is the evidence that the previous token survived. Without
 * one there is nothing to carry on with, so it falls through to the normal error
 * path rather than being silently swallowed.
 */
export function readMetaRedirect(args: {
  session: string | null;
  error: string | null;
  pagesGranted: string | null;
}): { session: string | null; fatalError: string | null; grantReturn: MetaGrantReturn } {
  const { session, error, pagesGranted } = args;

  if (error === META_NON_FATAL_ERRORS.adsScopeRevoked && session) {
    return { session, fatalError: null, grantReturn: { kind: 'ads_scope_revoked' } };
  }

  const grantReturn: MetaGrantReturn =
    pagesGranted === 'true' ? { kind: 'granted' }
      : pagesGranted === 'false' ? { kind: 'denied' }
        : { kind: 'none' };

  return { session, fatalError: error || null, grantReturn };
}

/**
 * The non-fatal notice for the step body, or `null` when there is nothing to say.
 *
 * Copy lives here rather than in JSX because on this feature the WORDING has
 * already been the defect once: a panel that was true sentence by sentence and
 * wrong as a whole. Keeping it beside the rule lets a test assert that the
 * sentence and the state agree.
 *
 * 🔴 Why this is separate from `grantPromptText` rather than folded into it: the
 * two speak about DIFFERENT PERMISSIONS. This one reports what happened to the
 * ADS permission; the card below is about PAGE access, under a heading that says
 * so. Filing an ads outcome under a Pages heading files it under the wrong
 * heading — the reader has to work out that the sentence is not about the thing
 * the box is named after. (It is NOT that the card fails to render: the revoke
 * path keeps the previous token, so `scopeGranted` stays false and the card is
 * drawn. Both are on screen together, and that is correct — two permissions, two
 * statements.)
 */
export function grantReturnNotice(grantReturn: MetaGrantReturn): string | null {
  if (grantReturn.kind !== 'ads_scope_revoked') return null;
  return (
    'The ads permission was switched off on Facebook’s consent screen, so we kept your '
    + 'existing connection and applied nothing. Your ad accounts are untouched. To link a '
    + 'Page, try again and leave every permission ticked — or skip the step.'
  );
}

/**
 * What the "Connect a Facebook Page" card should say above the button.
 *
 * 🔴 The default line ("we didn't ask for Page access when you connected") is a
 * statement about the PAST, and it becomes false the moment the user has just
 * tried. Saying it to someone who has been through the dialog and been refused
 * reads as the app not having noticed.
 */
export function grantPromptText(grantReturn: MetaGrantReturn): string {
  switch (grantReturn.kind) {
    case 'denied':
      return 'Facebook did not grant the Page permission, so we still can’t list your '
        + 'Pages. You can try again, or skip — your ad accounts are connected either way.';
    case 'ads_scope_revoked':
      return 'That attempt was discarded because the ads permission was switched off, so we '
        + 'still can’t list your Pages. Try again with every permission ticked, or skip.';
    case 'granted':
    case 'none':
      return 'We didn’t ask for Page access when you connected. Granting it lets us '
        + 'profile your brand’s public presence — your Page and the Instagram '
        + 'account linked to it.';
  }
}

// ---------------------------------------------------------------------------
// The re-grant round trip
// ---------------------------------------------------------------------------

/**
 * The wizard session carries TOKENS, not selections. The second Page grant is a
 * full server-side redirect through Meta, so the component unmounts and every
 * choice made so far is gone unless we park it ourselves.
 */
export const META_PAGE_GRANT_DRAFT_KEY = 'pending_meta_page_grant';

export interface MetaPageGrantDraft {
  /** The re-grant comes back on the SAME id, so a draft under a different one
   *  belongs to another connect attempt and must never be applied. */
  sessionId: string;
  adAccountIds: string[];
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStore(): StorageLike | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    // Storage can throw outright (blocked cookies, private mode).
    return null;
  }
}

export function writePageGrantDraft(
  draft: MetaPageGrantDraft,
  store: StorageLike | null = browserStore(),
): void {
  try {
    store?.setItem(META_PAGE_GRANT_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Losing the draft costs the user a re-pick, not the connection.
  }
}

export function readPageGrantDraft(
  sessionId: string,
  store: StorageLike | null = browserStore(),
): MetaPageGrantDraft | null {
  try {
    const raw = store?.getItem(META_PAGE_GRANT_DRAFT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const draft = parsed as Partial<MetaPageGrantDraft>;
    if (draft.sessionId !== sessionId) return null;
    if (!Array.isArray(draft.adAccountIds)) return null;
    const adAccountIds = draft.adAccountIds.filter((id): id is string => typeof id === 'string');
    // AFTER the filter, not before it. Step 1 cannot be left without an ad
    // account, so a draft carrying none is not a resumable wizard — and a draft
    // of `[123]` passes a length check and then filters down to nothing, landing
    // the user on the very step 2 this guard exists to keep them off.
    if (adAccountIds.length === 0) return null;
    return { sessionId, adAccountIds };
  } catch {
    return null;
  }
}

export function clearPageGrantDraft(store: StorageLike | null = browserStore()): void {
  try {
    store?.removeItem(META_PAGE_GRANT_DRAFT_KEY);
  } catch {
    // ignore
  }
}
